/**
 * Serac marketing top nav.
 *
 * Sticky, transparent until scroll, then dark with backdrop blur.
 * Wordmark + BUILD pill + nav links + GitHub stars + Sign in + Open dashboard.
 *
 * Lives outside MarketingHome so the Pricing page (and any future
 * marketing pages) can reuse the same nav without duplicating it.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Icon, SeracWordmark } from '../serac';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useMediaQuery';

interface SeracTopNavProps {
  /** Highlight a nav link by name (e.g. "pricing"). */
  active?: 'product' | 'integrations' | 'pricing' | 'use-cases' | 'blog' | 'docs' | 'changelog';
}

export default function SeracTopNav({ active }: SeracTopNavProps = {}) {
  const { t } = useTranslation(['marketing', 'common']);
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [stars, setStars] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const { isPortalAuthenticated } = useAuth();
  const isAuthenticated = isPortalAuthenticated;

  // Lock body scroll while the drawer is open. We freeze position +
  // top so iOS Safari doesn't bounce-scroll behind the menu, and we
  // restore the original scroll position on close. Plain
  // `overflow: hidden` is unreliable on iOS and on desktop browsers
  // that have a chunky scrollbar (the scrollbar disappearing shifts
  // the viewport width by 15px, which previously flipped the
  // useIsMobile media query and tripped the auto-close below — making
  // the drawer flash open and close instantly).
  useEffect(() => {
    if (!menuOpen) return;
    const scrollY = window.scrollY;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      document.body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [menuOpen]);

  // Close the menu when the viewport leaves mobile. Compare against
  // a derived value (not the raw isMobile) so transient scrollbar
  // shifts during the body-scroll lock don't trip a close.
  useEffect(() => {
    if (!menuOpen) return;
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setMenuOpen(false);
    }
  }, [isMobile, menuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Hit our own backend proxy instead of GitHub directly. The proxy
    // caches the count server-side for 1h, so visitors no longer share
    // the GitHub 60/hr/IP rate limit (which was burning out on shared
    // NAT networks like mobile carriers and corporate VPNs and falling
    // back to the placeholder "Star" text on production).
    //
    // Absolute URL: the marketing host (serac.build, Cloudflare Pages)
    // has no backend, so a relative fetch would 404. The dashboard host
    // is the only place /api lives, so we hit it directly from either.
    const isApex =
      typeof window !== 'undefined' &&
      /^(www\.)?serac\.build$/.test(window.location.hostname);
    const starsUrl = isApex
      ? 'https://dashboard.serac.build/api/github-stars'
      : '/api/github-stars';
    // Defer the fetch until the browser is idle so it never sits on
    // the marketing critical path. The badge is decorative — falling
    // back to "Star" while the page loads is fine. Safari doesn't ship
    // requestIdleCallback yet; fall back to a setTimeout there.
    const idle =
      (typeof window !== 'undefined' && (window as Window & { requestIdleCallback?: typeof requestIdleCallback }).requestIdleCallback) ||
      ((cb: () => void) => setTimeout(cb, 1500));
    const handle = idle(() => {
      fetch(starsUrl)
        .then(r => (r.ok ? r.json() : null))
        .then((data: { success?: boolean; formatted?: string } | null) => {
          if (data?.success && typeof data.formatted === 'string') {
            setStars(data.formatted);
          }
        })
        .catch(() => {});
    });
    return () => {
      const cancel =
        (typeof window !== 'undefined' && (window as Window & { cancelIdleCallback?: typeof cancelIdleCallback }).cancelIdleCallback) ||
        clearTimeout;
      cancel(handle as number);
    };
  }, []);

  const linkStyle: CSSProperties = {
    fontSize: 13,
    color: 'var(--fg-muted)',
    padding: '6px 10px',
    borderRadius: 6,
    letterSpacing: '-0.005em',
    cursor: 'pointer',
    transition: 'color 150ms',
    textDecoration: 'none',
  };
  const activeLinkStyle: CSSProperties = { ...linkStyle, color: 'var(--fg)' };

  const isMarketingApex = () =>
    typeof window !== 'undefined' && /^(www\.)?serac\.build$/.test(window.location.hostname);
  const portalHref = (path: string) =>
    isMarketingApex() ? `https://dashboard.serac.build${path}` : null;

  const onOpenApp = () => {
    const path = isAuthenticated ? '/portal/dashboard' : '/login';
    const href = portalHref(path);
    if (href) {
      window.location.href = href;
      return;
    }
    navigate(path);
  };
  const onSignIn = () => {
    const href = portalHref('/login');
    if (href) {
      window.location.href = href;
      return;
    }
    navigate('/login');
  };
  const LABS_URL = 'https://seraclabs.dev';

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        // Above the open mobile menu (z:39) so the X close button
        // stays clickable and visible. Without the bump the
        // transparent nav background let the menu's backdrop-blur
        // show through where the X sits, and the icon read as
        // "behind" the menu.
        zIndex: 41,
        // When the mobile menu is open, force a solid blurred chrome
        // even at the top of the page (where `scrolled` is false and
        // the bar would otherwise be transparent). Otherwise the X
        // close button visually disappears into the menu's
        // backdrop-blur underneath the transparent nav.
        background:
          menuOpen || scrolled
            ? 'color-mix(in oklch, var(--bg) 75%, transparent)'
            : 'transparent',
        backdropFilter: menuOpen || scrolled ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: menuOpen || scrolled ? 'blur(12px)' : 'none',
        borderBottom: `1px solid ${menuOpen || scrolled ? 'var(--border)' : 'transparent'}`,
        transition: 'background 180ms, border 180ms',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '12px 16px' : '14px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 24 }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <SeracWordmark size={isMobile ? 16 : 18} />
          </Link>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--fg-dim)',
              padding: '2px 8px',
              border: '1px solid var(--border)',
              borderRadius: 999,
              letterSpacing: '0.06em',
            }}
          >
            {t('marketing:nav.build')}
          </span>
        </div>
        {!isMobile && (
          <nav style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link to="/#product" style={active === 'product' ? activeLinkStyle : linkStyle}>
              {t('marketing:nav.product')}
            </Link>
            <Link
              to="/#integrations"
              style={active === 'integrations' ? activeLinkStyle : linkStyle}
            >
              {t('marketing:nav.integrations')}
            </Link>
            <Link to="/pricing" style={active === 'pricing' ? activeLinkStyle : linkStyle}>
              {t('marketing:nav.pricing')}
            </Link>
            <Link
              to="/use-cases/ai-in-servicenow"
              style={active === 'use-cases' ? activeLinkStyle : linkStyle}
            >
              {t('marketing:nav.useCases')}
            </Link>
            <Link to="/blog" style={active === 'blog' ? activeLinkStyle : linkStyle}>
              {t('marketing:nav.blog')}
            </Link>
            <a
              style={active === 'docs' ? activeLinkStyle : linkStyle}
              href="https://docs.serac.build"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('marketing:nav.docs')}
            </a>
            <a
              style={active === 'changelog' ? activeLinkStyle : linkStyle}
              href="https://github.com/serac-labs/serac/releases"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('marketing:nav.changelog')}
            </a>
            <a
              style={linkStyle}
              href={LABS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('marketing:nav.company')}
            </a>
          </nav>
        )}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a
              href="https://github.com/serac-labs/serac"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <Button variant="ghost" size="sm" icon={<Icon.github s={13} />}>
                {stars ?? t('marketing:nav.stars')}
              </Button>
            </a>
            {!isAuthenticated && (
              <Button variant="quiet" size="sm" onClick={onSignIn}>
                {t('marketing:nav.signIn')}
              </Button>
            )}
            <Button variant="ice" size="sm" onClick={onOpenApp} iconRight={<Icon.arrow s={12} />}>
              {t('marketing:nav.openDashboard')}
            </Button>
          </div>
        )}
        {isMobile && (
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--fg)',
              borderRadius: 8,
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              {menuOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        )}
      </div>
      {isMobile && menuOpen && createPortal(
        // Rendered into <body> so no ancestor (sticky nav, backdrop-
        // filter, transform) can create a containing block that
        // confines this fixed-positioned drawer to the small nav strip.
        // Previously we lived inside the sticky parent and Safari
        // pinned the drawer to the nav's bounding box, making it look
        // like the menu opened "for a flash" then disappeared into a
        // 0×0 region.
        //
        // z-index sits BELOW the sticky nav (41) and ChromeBar (50)
        // so the X close button + brand stay visible and clickable
        // on top of the drawer.
        <div
          style={{
            position: 'fixed',
            inset: 0,
            // ChromeBar (40) + nav (~56) = 96 at the top of the page;
            // once we've scrolled past the chrome bar only the sticky
            // nav remains, so 56 is enough.
            paddingTop: scrolled ? 56 : 96,
            background: 'var(--bg)',
            zIndex: 30,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => {
            // Clicks on the backdrop close the menu; clicks on actual
            // nav items still bubble to their own handlers.
            if (e.target === e.currentTarget) setMenuOpen(false);
          }}
        >
          <div
            style={{
              padding: '20px 16px 32px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <Link to="/#product" onClick={() => setMenuOpen(false)} style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}>
              {t('marketing:nav.product')}
            </Link>
            <Link to="/#integrations" onClick={() => setMenuOpen(false)} style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}>
              {t('marketing:nav.integrations')}
            </Link>
            <Link to="/pricing" onClick={() => setMenuOpen(false)} style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}>
              {t('marketing:nav.pricing')}
            </Link>
            <Link to="/use-cases/ai-in-servicenow" onClick={() => setMenuOpen(false)} style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}>
              {t('marketing:nav.useCases')}
            </Link>
            <Link to="/blog" onClick={() => setMenuOpen(false)} style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}>
              {t('marketing:nav.blog')}
            </Link>
            <a href="https://docs.serac.build" target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}>
              {t('marketing:nav.docs')}
            </a>
            <a href="https://github.com/serac-labs/serac/releases" target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}>
              {t('marketing:nav.changelog')}
            </a>
            <a
              href={LABS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              style={{ ...linkStyle, fontSize: 16, padding: '12px 8px' }}
            >
              {t('marketing:nav.company')}
            </a>
            <div style={{ height: 1, background: 'var(--border)', margin: '12px 0' }} />
            <a
              href="https://github.com/serac-labs/serac"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="ghost" size="sm" icon={<Icon.github s={13} />}>
                {stars ?? t('marketing:nav.stars')}
              </Button>
            </a>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {!isAuthenticated && (
                <Button variant="quiet" size="sm" onClick={() => { setMenuOpen(false); onSignIn(); }}>
                  {t('marketing:nav.signIn')}
                </Button>
              )}
              <Button variant="ice" size="sm" onClick={() => { setMenuOpen(false); onOpenApp(); }} iconRight={<Icon.arrow s={12} />}>
                {t('marketing:nav.openDashboard')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
